<template>
  <div class="flex h-screen max-h-screen bg-white">
    <!-- Sidebar -->
    <CorpSidebar v-if="showSidebar" />

    <!-- Main Content -->
    <main
      id="main-view"
      class="w-full h-full flex-1 flex flex-col overflow-hidden"
    >
      <CorpToolbar v-if="showToolbar" class="flex-shrink-0" />

      <div
        class="flex flex-1 min-h-0 flex-col overflow-y-auto"
        :class="mainContentScrollClass"
      >
        <RouterView
          v-slot="{ Component }"
          :key="$route.path"
        >
          <component
            :is="Component"
            class="flex w-full min-h-0 flex-1 flex-col"
          />
        </RouterView>
      </div>
    </main>
  </div>

  <!-- Small Screen Banner -->
  <EcxSmallScreenBanner
    v-if="showSmallScreenBanner"
    :title="t('corp.small_screen.title')"
    :message="t('corp.small_screen.message')"
    :exit-button-text="t('corp.small_screen.exit_button')"
    logo-src="/assets/images/ecx_logo.webp"
  />

  <!-- Toast Container -->
  <EcxFeedbackToastContainer />

  <BulkImportProgressToast
    v-if="route.name !== 'bulk-import-central'"
    @view-progress="(item) => bulkImportStore.reopenById(item.entity, item.id)"
    @reopen="(item) => bulkImportStore.reopenById(item.entity, item.id)"
  />

  <BulkExportToast />

  <BulkImportDrawer
    :open="bulkImportStore.drawerOpen"
    :entity="bulkImportStore.entity"
    :can-import="can(bulkImportStore.entity, 'import')"
    persist-on-close
    @update:open="bulkImportStore.setDrawerOpen"
    @select-manual="bulkImportStore.requestManualAdd"
    @navigate-users="handleBulkNavigateUsers"
    @navigate-vehicles="handleBulkNavigateVehicles"
    @navigate-cards="handleBulkNavigateCards"
    @minimize="bulkImportStore.setDrawerOpen(false)"
  />
</template>

<script setup lang="ts">
import { computed, watch, onMounted, onBeforeUnmount } from 'vue'
import { RouterView, useRoute, useRouter, type RouteLocationNormalizedLoaded } from 'vue-router'
import { useI18n } from 'vue-i18n'

import CorpSidebar from '@/components/navigation/CorpSidebar.vue'
import CorpToolbar from '@/components/navigation/CorpToolbar.vue'
import { BulkImportProgressToast, BulkExportToast, BulkImportDrawer } from '@/components/bulkImport'

import { EcxSmallScreenBanner, EcxFeedbackToastContainer, useFeedbackToast } from '@ecx/ui'

import { useCompanyStore } from '@/stores/company'
import { useAuthStore } from '@/stores/auth'
import { useBulkImportProgressPoll } from '@/composables/bulkImport/useBulkImportProgressPoll'
import { useBulkImportStore } from '@/stores/bulkImport'
import { useActiveBulkImportsStore } from '@/stores/activeBulkImports'
import { useManagerCan } from '@/composables/users/useManagerCan'
import { useIntercomMessenger } from '@/composables/intercom/useIntercomMessenger'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const companyStore = useCompanyStore()
const authStore = useAuthStore()
const bulkImportPoll = useBulkImportProgressPoll()
const bulkImportStore = useBulkImportStore()
const activeBulkImportsStore = useActiveBulkImportsStore()
const { can } = useManagerCan()
const { error: showBulkImportErrorToast } = useFeedbackToast()
useIntercomMessenger()

watch(
  () => [bulkImportStore.drawerOpen, bulkImportStore.step, bulkImportStore.currentImport?.id] as const,
  ([open, step, id]) => {
    if (open && id && (step === 'result_success' || step === 'result_partial')) {
      activeBulkImportsStore.dismiss(id)
    }
  },
)

// O drawer é global; o erro precisa aparecer em qualquer rota, não só em /usuarios.
watch(
  () => bulkImportStore.error,
  (message) => {
    if (message) {
      showBulkImportErrorToast(t('corp.bulk_import.error_title'), {
        description: message,
        primaryAction: { label: t('corp.bulk_import.toast.btn_close') },
      })
    }
  },
)

function handleBulkNavigateUsers(importId: string) {
  bulkImportStore.setDrawerOpen(false)
  router.push({ name: 'managed-users', query: { importacao: importId } })
}

function handleBulkNavigateVehicles(importId: string) {
  bulkImportStore.setDrawerOpen(false)
  router.push({ name: 'vehicles', query: { importacao: importId } })
}

function handleBulkNavigateCards(importId: string) {
  bulkImportStore.setDrawerOpen(false)
  router.push({ name: 'cards', query: { importacao: importId } })
}

const mainContentScrollClass = computed(() =>
  route.meta.scrollMainContent === false ? 'overflow-hidden' : 'overflow-y-auto',
)

// Computed para controlar quando mostrar sidebar/toolbar
const showSidebar = computed(() => route.meta.showSidebar !== false)
const showToolbar = computed(() => route.meta.showToolbar !== false)
/** No documento do iframe a viewport é estreita; `desktop:hidden` do banner dispararia em falso. */
function isAppEmbeddedInIframe(): boolean {
  if (typeof window === 'undefined') {
    return false
  }
  try {
    return window.self !== window.top
  } catch {
    return true
  }
}

/** Webviews / iframes estreitos (token SWAP, Flutter); nunca bloquear com banner de “tela pequena”. */
function isSwapVirtualCardViewerRoute(routeRef: RouteLocationNormalizedLoaded): boolean {
  const routeName = routeRef.name
  if (routeName === 'swap-virtual-card' || routeName === 'swap-virtual-card-mobile') {
    return true
  }
  if (routeRef.path === '/swap-virtual-card') {
    return true
  }
  return routeRef.path.startsWith('/app/dados-cartao')
}

const showSmallScreenBanner = computed(() => {
  if (isAppEmbeddedInIframe()) {
    return false
  }
  if (isSwapVirtualCardViewerRoute(route)) {
    return false
  }
  return route.meta.showSmallScreenBanner !== false
})

// Efeitos do app autenticado (lista de empresas + poll de bulk import). Duas
// condições, ambas necessárias:
// - route.meta.requiresAuth === false → rota pública/viewer: não roda side-effect de
//   shell (ex.: webview /app/dados-cartao). Confiável agora que o app monta após
//   router.isReady (antes falhava no START_LOCATION, com requiresAuth undefined).
// - !authStore.isAuthenticated → sem sessão: não dispara chamada autenticada (evita
//   GET /manager/accounts sem sessão → 401 → redirect ao login).
onMounted(async () => {
  if (route.meta.requiresAuth === false || !authStore.isAuthenticated) return
  bulkImportPoll.start()
  await companyStore.fetchCompanies()
})

onBeforeUnmount(() => {
  bulkImportPoll.stop()
})
</script>

<style>
@import '@/styles/index.css';
</style>
